import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FreeAuditButton } from "@/components/ui/Cta";
import { ADVISORY_FORM_PATH, LINK_HUB_PATH, QUIZ_PATH } from "@/config/constants";
import { useAppTranslation } from "@/i18n/useAppTranslation";

/** Pages that already are a single action, where a second button only competes. */
const HIDDEN_ON = new Set<string>([ADVISORY_FORM_PATH, LINK_HUB_PATH, QUIZ_PATH]);

/** Scroll distance after which the hero's own button is out of view. */
const SHOW_AFTER = 640;

/**
 * Phones only: once the hero scrolls away, the free audit stays one tap away.
 * Leaves room on the right for Titi's launcher and steps aside while the
 * closing call to action is on screen.
 */
export function MobileCtaBar() {
  const { pathname } = useLocation();
  const { t } = useAppTranslation("common");
  const [scrolled, setScrolled] = useState(false);
  const [finalInView, setFinalInView] = useState(false);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      setScrolled(window.scrollY > SHOW_AFTER);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [pathname]);

  useEffect(() => {
    const target = document.getElementById("auditoria");
    if (!target) {
      setFinalInView(false);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setFinalInView(entry.isIntersecting), { threshold: 0.15 });
    observer.observe(target);
    return () => observer.disconnect();
  }, [pathname]);

  if (HIDDEN_ON.has(pathname)) return null;
  const visible = scrolled && !finalInView;

  return (
    <div
      role="region"
      aria-label={t("mobileCta")}
      aria-hidden={!visible}
      inert={!visible}
      className={`fixed bottom-5 left-4 right-24 z-[80] transition-[opacity,transform] duration-300 lg:hidden ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <FreeAuditButton
        location="mobile_bar"
        size="md"
        className="w-full justify-center !min-h-13 shadow-lit-lg"
      />
    </div>
  );
}
